<template>
  <section class="admin-page">

    <!-- Header -->
    <header class="admin-header">
      <h1>Broadcast Monitor</h1>
      <p>Monitor delivery, scale, and abuse signals across all creator broadcasts.</p>
    </header>

    <!-- Filters -->
    <section class="filters">
      <input v-model="filters.search" placeholder="Search creator…" />

      <select v-model="filters.channel">
        <option value="">All Channels</option>
        <option value="email">Email</option>
        <option value="sms">SMS</option>
        <option value="push">Push</option>
      </select>

      <select v-model="filters.status">
        <option value="">All Status</option>
        <option value="sent">Sent</option>
        <option value="delivered">Delivered</option>
        <option value="failed">Failed</option>
        <option value="flagged">Flagged</option>
      </select>

      <input type="date" v-model="filters.from" />
      <input type="date" v-model="filters.to" />
    </section>

    <!-- KPI Strip -->
    <section class="kpis">
      <div class="kpi">
        <p>Total Broadcasts</p>
        <h2>{{ stats.total }}</h2>
      </div>
      <div class="kpi">
        <p>Delivered</p>
        <h2>{{ stats.delivered }}</h2>
      </div>
      <div class="kpi">
        <p>Failures</p>
        <h2 class="warn">{{ stats.failed }}</h2>
      </div>
      <div class="kpi">
        <p>Flagged</p>
        <h2 class="danger">{{ stats.flagged }}</h2>
      </div>
    </section>

    <!-- Broadcast Table -->
    <section class="card">
      <table class="broadcast-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Creator</th>
            <th>Channel</th>
            <th>Audience</th>
            <th>Delivery</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="b in broadcasts" :key="b.broadcast_id">
            <td>{{ b.bc_datetime }}</td>

            <td>
              <strong>{{ b.creator_name }}</strong>
              <div class="sub">{{ b.creator_email }}</div>
            </td>

            <td>{{ b.bc_channel || "OTHER"}}</td>
            <td>{{ b.bc_audience }}</td>
            <td>{{ b.bc_delivery_rate }}%</td>

            <td>
              <span :class="['status-pill', b.bc_status]">
                {{ b.bc_status }}
              </span>
            </td>

            <td class="actions">
              <button
                v-if="b.bc_status === 'flagged'"
                @click="review(b)"
              >
                Review
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="broadcasts.length === 0" class="empty">
        No broadcasts found.
      </div>
    </section>

  </section>
</template>

<script>
import { getBroadcasts, getBroadcastStats, reviewBroadcast } from "@/apis/broadcastapis";
import { useToast } from "vue-toastification";

export default {
  name: "AdminBroadcastMonitor",

  data() {
    return {
      toast: useToast(),

      filters: {
        search: "",
        channel: "",
        status: "",
        from: "",
        to: ""
      },

      stats: {
        total: 0,
        delivered: 0,
        failed: 0,
        flagged: 0
      },

      broadcasts: [],
      loading: false
    };
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetchBroadcasts();
      }
    }
  },

  mounted() {
    this.fetchBroadcasts();
    this.fetchStats();
  },

  methods: {

    async fetchBroadcasts() {

      this.loading = true;

      const res = await getBroadcasts({
        page_no: 0,
        search: this.filters.search,
        channel: this.filters.channel,
        status: this.filters.status,
        from: this.filters.from,
        to: this.filters.to
      });

      this.loading = false;

      if (!res.status) {
        this.toast.error(res.success || "Failed loading broadcasts");
        return;
      }

      this.broadcasts = res.records || [];
    },

    async fetchStats() {

      const res = await getBroadcastStats();

      if (!res.status) {
        this.toast.error("Stats load failed");
        return;
      }

      this.stats = res.stats;
    },

    async review(broadcast) {

      const res = await reviewBroadcast(broadcast.broadcast_id, "clear");

      if (!res.status) {
        this.toast.error(res.success || "Review failed");
        return;
      }

      this.toast.success("Broadcast reviewed");

      this.fetchBroadcasts();
      this.fetchStats();
    }
  }
};
</script>

<style scoped>
.admin-page {
  max-width: 1440px;
  margin: auto;
  padding: 48px;
}

.admin-header h1 {
  font-size: 36px;
  font-weight: 800;
}

.admin-header p {
  color: #64748b;
  margin-top: 6px;
}

/* Filters */
.filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  margin: 32px 0;
}

.filters input,
.filters select {
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  font-size: 14px;
}

/* KPIs */
.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.kpi {
  background: white;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.kpi p {
  font-size: 11px;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
}

.kpi h2 {
  font-size: 26px;
  font-weight: 800;
}

.kpi h2.warn {
  color: #92400e;
}

.kpi h2.danger {
  color: #991b1b;
}

/* Card */
.card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  border: 1px solid #e5e7eb;
}

/* Table */
.broadcast-table {
  width: 100%;
  border-collapse: collapse;
}

.broadcast-table th,
.broadcast-table td {
  padding: 14px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.sub {
  font-size: 12px;
  color: #94a3b8;
}

/* Status pills */
.status-pill {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  text-transform: uppercase;
}

.status-pill.delivered {
  background: #ecfdf5;
  color: #047857;
}

.status-pill.failed {
  background: #fef2f2;
  color: #991b1b;
}

.status-pill.flagged {
  background: #fffbeb;
  color: #92400e;
}

/* Actions */
.actions button {
  font-weight: 700;
  color: #0f172a;
}

/* Empty */
.empty {
  padding: 24px;
  text-align: center;
  font-size: 14px;
  color: #64748b;
}

/* Mobile */
@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr;
  }

  .kpis {
    grid-template-columns: 1fr 1fr;
  }

  .admin-page {
    padding: 24px;
  }
}
/* ===============================
   ADMIN BROADCAST MONITOR – MOBILE
   =============================== */
@media (max-width: 640px) {

  /* Page padding */
  .admin-page {
    padding: 20px 16px 72px;
  }

  /* Header */
  .admin-header h1 {
    font-size: 26px;
    line-height: 1.2;
  }

  .admin-header p {
    font-size: 14px;
  }

  /* Filters → stacked & compact */
  .filters {
    gap: 10px;
    margin: 20px 0;
  }

  .filters input,
  .filters select {
    padding: 12px;
    font-size: 14px;
    border-radius: 10px;
  }

  /* KPI strip */
  .kpis {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 24px;
  }

  .kpi {
    padding: 16px;
    border-radius: 14px;
  }

  .kpi h2 {
    font-size: 22px;
  }

  /* Card */
  .card {
    padding: 16px;
    border-radius: 18px;
  }

  /* Table → stacked rows */
  .broadcast-table thead {
    display: none;
  }

  .broadcast-table,
  .broadcast-table tbody,
  .broadcast-table tr,
  .broadcast-table td {
    display: block;
    width: 100%;
  }

  .broadcast-table tr {
    padding: 14px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .broadcast-table td {
    padding: 6px 0;
    border: none;
    font-size: 14px;
  }

  .broadcast-table td strong {
    font-size: 15px;
  }

  .sub {
    font-size: 12px;
  }

  /* Status pills */
  .status-pill {
    font-size: 10px;
    padding: 4px 8px;
  }

  /* Actions */
  .actions {
    margin-top: 6px;
  }

  .actions button {
    width: 100%;
    padding: 10px 14px;
    border-radius: 12px;
    background: #f1f5f9;
    text-align: center;
  }

  /* Empty state */
  .empty {
    padding: 20px 10px;
    font-size: 14px;
  }
}

</style>
